/**
 * CR-015 (anh Tiến 10/10/2026): nội dung in trên mẫu báo giá (PDF / Excel gửi khách).
 * - Mỗi dòng có 2 chỗ sửa mềm khi in: "Tên hàng – Quy cách" và "ĐVT"; mã hàng giữ ngầm định (không in).
 * - Thép tấm in qua lại theo Tấm hoặc Kg (dòng vẫn lưu theo Kg, thành tiền không đổi).
 * - Quạt: Q, H một dòng; P, điện áp, chịu nhiệt một dòng.
 * - Ống gió (trơn, EI): dòng hình dạng + kích thước (mm) và dòng vật liệu + độ dày; bỏ hao hụt, m², kg, đơn giá.
 * Kích thước in mặc định theo mm. Dữ liệu dòng (SL, ĐVT, giá) không đổi; Excel nhập Asia không dùng phần này.
 */
import { formatQty } from './format';
import { isFanName } from './fan';
import type { DuctworkSnapshot } from './ductwork';

export type PlatePrintBy = 'kg' | 'sheet';

/** quote_items.print_opts: thiếu khóa / null = in tự động */
export interface QuotePrintOpts {
  /** Tên hàng – Quy cách in, nhiều dòng (dòng đầu là tên hàng) */
  desc?: string | null;
  /** ĐVT in */
  unit?: string | null;
  /** Xuất xứ in (mặc định theo khai báo: hãng → xuất xứ → ký hiệu hãng trong mã) */
  origin?: string | null;
  /** Thép tấm: in theo Kg (mặc định) hay theo Tấm */
  plateBy?: PlatePrintBy | null;
}

export const PRINT_DESC_MAX = 1000;
export const PRINT_UNIT_MAX = 20;
export const PRINT_ORIGIN_MAX = 100;

/** Bỏ khóa rỗng; không còn gì thì null (in tự động). */
export function normalizePrintOpts(o: QuotePrintOpts | null | undefined): QuotePrintOpts | null {
  if (!o) return null;
  const desc = o.desc
    ?.replace(/\r\n?/g, '\n')
    .split('\n')
    .map((s) => s.replace(/\s+/g, ' ').trim())
    .filter(Boolean)
    .join('\n')
    .slice(0, PRINT_DESC_MAX);
  const unit = o.unit?.replace(/\s+/g, ' ').trim().slice(0, PRINT_UNIT_MAX);
  const origin = o.origin?.replace(/\s+/g, ' ').trim().slice(0, PRINT_ORIGIN_MAX);
  const out: QuotePrintOpts = {};
  if (desc) out.desc = desc;
  if (unit) out.unit = unit;
  if (origin) out.origin = origin;
  if (o.plateBy === 'sheet') out.plateBy = 'sheet';
  return Object.keys(out).length ? out : null;
}

type PrintLang = 'vi' | 'en' | 'zh';
const WORDS: Record<'voltage' | 'heat' | 'speed' | 'material' | 'thick' | 'layers', Record<PrintLang, string>> = {
  voltage: { vi: 'Điện áp', en: 'Voltage', zh: '电压' },
  heat: { vi: 'chịu nhiệt', en: 'heat resistance', zh: '耐温' },
  speed: { vi: 'Tốc độ', en: 'Speed', zh: '转速' },
  material: { vi: 'Vật liệu', en: 'Material', zh: '材料' },
  thick: { vi: 'dày', en: 'thickness', zh: '厚' },
  layers: { vi: 'lớp', en: 'layer(s)', zh: '层' },
};

type Num = (v: number) => string;
const vnNum: Num = (v) => formatQty(v);
/** Số mm in liền, không dấu nhóm nghìn ("1110", "0,75") như cách ghi bản vẽ */
const mmNum = (v: number) => (Math.round(v * 100) / 100).toString().replace('.', ',');

/**
 * Quy cách quạt → các dòng in: "Q=25.000 m3/h, H=150 Pa" / "P=2,2 kW, Điện áp 380V/3 pha, chịu nhiệt 300°C" / "Tốc độ 1.450 v/p".
 * Đọc theo định dạng fanSpecText ("Q=…, H=…, P=…, 380V/3 pha, 1.450 v/p, 300"); ý không nhận ra được đưa xuống dòng cuối.
 */
export function fanPrintLines(spec: string | null | undefined, lang: PrintLang = 'vi'): string[] {
  const parts = (spec ?? '')
    .split(/,\s+(?=\S)|;\s*|\n/)
    .map((s) => s.trim())
    .filter(Boolean);
  const qh: string[] = [];
  const power: string[] = [];
  const rest: string[] = [];
  let heat: string | null = null;
  let seenVoltage = false;
  for (const p of parts) {
    if (/^[QH]\s*=/i.test(p)) qh.push(p.replace(/\s*=\s*/, '='));
    else if (/^P\s*=/i.test(p)) power.unshift(p.replace(/\s*=\s*/, '='));
    else if (/^\d+\s*V\b/i.test(p) || /điện áp|voltage/i.test(p)) {
      seenVoltage = true;
      power.push(/điện áp|voltage|电压/i.test(p) ? p : `${WORDS.voltage[lang]} ${p}`);
    } else if (/v\/p|rpm|vòng/i.test(p)) rest.push(/tốc độ|speed|转速/i.test(p) ? p : `${WORDS.speed[lang]} ${p}`);
    else if (/°|độ\s*c|chịu nhiệt|heat|耐温/i.test(p) || (seenVoltage && /^\d+([.,]\d+)?(\s*\/\s*\d+\s*h)?$/i.test(p))) heat = p;
    else rest.push(p);
  }
  if (heat) {
    const h = /chịu nhiệt|heat|耐温/i.test(heat)
      ? heat
      : `${WORDS.heat[lang]} ${heat.replace(/^(\d+(?:[.,]\d+)?)\s*(?:độ\s*c|°\s*c|°)?/i, '$1°C')}`;
    power.push(h);
  }
  return [qh.join(', '), power.join(', '), ...rest].filter(Boolean);
}

/** Kích thước in theo mm. Ống thẳng: "500×700, L=1100mm", "D500, L=3.000mm"; loại khác giữ mô tả của bộ tính (đã là mm). */
function ductDims(snap: DuctworkSnapshot): string {
  const n = mmNum;
  const r = snap.result;
  const d = r.dimensionsMm ?? {};
  if (r.calcType === 'rect_straight' && d.W && d.H && d.L) return `${n(d.W)}×${n(d.H)}, L=${n(d.L)}mm`;
  if (r.calcType === 'round_straight' && d.D && d.L) return `D${n(d.D)}, L=${n(d.L)}mm`;
  return r.dimensionText;
}

/**
 * Ống gió / phụ kiện → các dòng in: "Ống thẳng vuông/chữ nhật 500×700, L=1.100mm" / "Vật liệu: Tôn mạ kẽm (GI), dày 0,75 mm".
 * Ống EI thêm lớp tấm chống cháy vào dòng vật liệu. Không in hao hụt, m², kg, đơn giá (anh Tiến 10/10/2026).
 */
export function ductPrintLines(snap: DuctworkSnapshot | null | undefined, lang: PrintLang = 'vi', n: Num = vnNum): string[] {
  if (!snap?.result) return [];
  const r = snap.result;
  const lines = [`${r.label} ${ductDims(snap)}`.trim()];
  const material = snap.material?.name ?? r.materialName ?? r.materialCode;
  const mat = [material, r.thicknessMm ? `${WORDS.thick[lang]} ${n(r.thicknessMm)} mm` : null].filter(Boolean).join(', ');
  const ei = snap.ei ? `${snap.ei.board.rating} ${snap.ei.board.name} × ${snap.ei.layers} ${WORDS.layers[lang]}` : null;
  const matLine = [mat, ei].filter(Boolean).join(' + ');
  if (matLine) lines.push(`${WORDS.material[lang]}: ${matLine}`);
  return lines;
}

/** Kích thước tấm theo mm: "500 × 1500 × 8 mm" (dài × rộng × dày). */
export function plateDimsMm(p: { lengthM: number; widthM: number; thicknessMm?: number | null }): string {
  const mm = (m: number) => mmNum(m * 1000);
  return `${mm(p.lengthM)} × ${mm(p.widthM)}${p.thicknessMm ? ` × ${mmNum(p.thicknessMm)}` : ''} mm`;
}

/** Quy cách in tự động (không gồm tên hàng) của dòng quạt / ống gió; null = dòng thường (in quy cách nguyên văn). */
export function autoSpecLines(
  l: { productName: string; spec?: string | null; ductwork?: DuctworkSnapshot | null | Record<string, unknown> },
  lang: PrintLang = 'vi',
  n: Num = vnNum,
): string[] | null {
  const snap = l.ductwork as DuctworkSnapshot | null | undefined;
  if (snap?.result) return ductPrintLines(snap, lang, n);
  if (isFanName(l.productName)) return fanPrintLines(l.spec, lang);
  return null;
}

/** Nội dung "Tên hàng – Quy cách" in tự động bằng tiếng Việt (điền sẵn trong hộp "Nội dung in" để NV sửa). */
export function autoPrintDesc(l: { productName: string; spec?: string | null; standard?: string | null; ductwork?: DuctworkSnapshot | null | Record<string, unknown> }): string {
  const lines = autoSpecLines(l) ?? (l.spec?.trim() ? [l.spec.trim()] : []);
  return [l.productName.trim(), ...lines, l.standard?.trim() ? `TC: ${l.standard.trim()}` : null].filter(Boolean).join('\n');
}

/**
 * CR-011 (anh Tiến 07/10/2026): "Xuất xứ = Thương hiệu = Hãng", lấy theo mã hàng.
 * Mã Asia mở đầu bằng ký hiệu hãng (KHP Hòa Phát, KHS Hoa Sen, KVD Việt Đức, KYF hàng Trung Quốc) → dùng khi dòng chưa có hãng.
 */
const BRAND_BY_CODE_PREFIX: [string, string][] = [
  ['KHP', 'Hòa Phát'],
  ['KHS', 'Hoa Sen'],
  ['KVD', 'Việt Đức'],
  ['KYF', 'Trung Quốc'],
];
export function brandFromCode(code: string | null | undefined): string | null {
  const c = code?.trim().toUpperCase();
  return (c && BRAND_BY_CODE_PREFIX.find(([p]) => c.startsWith(p))?.[1]) || null;
}
/** Cột Xuất xứ: hãng của mã (chụp trên dòng) → xuất xứ của mã → ký hiệu hãng trong mã Asia. */
export function autoOrigin(i: { brand?: string | null; origin?: string | null; productCode?: string | null; productName?: string | null }, sep = '\n'): string {
  // Quạt (anh Tiến 08/10/2026): in cả thương hiệu và xuất xứ, VD "Minh Tiến / Việt Nam"
  if (isFanName(i.productName)) {
    const both = [i.brand?.trim(), i.origin?.trim()].filter((v, k, a): v is string => !!v && a.findIndex((x) => x?.toLowerCase() === v.toLowerCase()) === k);
    if (both.length) return both.join(sep);
  }
  return i.brand?.trim() || i.origin?.trim() || brandFromCode(i.productCode) || '';
}

/** Độ rộng ước lượng (đơn vị em) của chữ Times: hoa ~0,7, thường/số ~0,5, chữ Hán 1. */
export function serifTextEm(text: string): number {
  let w = 0;
  for (const ch of text) {
    if (/[\u3000-\u9fff\uff00-\uffef]/.test(ch)) w += 1;
    else if (/\p{Lu}/u.test(ch)) w += 0.7;
    else if (/\s/.test(ch)) w += 0.25;
    else w += 0.5;
  }
  return w;
}

/** CR-015 mục 3: cỡ chữ ô ĐVT chuẩn 12 (Times New Roman), tự co cho vừa ô rộng `maxWidthPt`; không nhỏ hơn `min`. */
export function fitUnitFontSize(text: string, maxWidthPt: number, base = 12, min = 6.5): number {
  const em = Math.max(0, ...text.split('\n').map(serifTextEm));
  if (!em) return base;
  return Math.max(min, Math.min(base, Math.floor((maxWidthPt / em) * 2) / 2));
}
